import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useAuthStore from '../stores/authStore'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Card from '../components/ui/Card'
import GoogleOAuth from '../components/GoogleOAuth'
import { GOOGLE_CLIENT_ID } from '../utils/constants'

const LoginPage = () => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  
  const { login, isLoading } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  
  const from = location.state?.from?.pathname || '/dashboard'

  const handleGoogleSuccess = (result) => {
    navigate(from, { replace: true })
  }

  const handleGoogleError = (error) => {
    setErrors({ general: error })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})
    
    if (!email || !password) {
      setErrors({
        email: !email ? t('validation.emailRequired') : '',
        password: !password ? t('validation.passwordRequired') : '',
      })
      return
    }

    const result = await login(email, password)
    if (result.success) {
      navigate(from, { replace: true })
    } else {
      setErrors({ general: result.error })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center mb-8">
            <div className="bg-white dark:bg-white rounded-lg p-2 shadow-sm">
              <img 
                src="/logo.png" 
                alt="T8R - AI Translation Platform" 
                className="h-16 w-auto"
              />
            </div>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('login.welcomeBack')}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('login.signInToAccount')}
          </p>
        </div>

        <Card>
          <Card.Content>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
                </div>
              )}

              <Input
                label={t('form.emailAddress')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                placeholder={t('form.enterEmail')}
                autoComplete="email"
              />

              <div className="relative">
                <Input
                  label={t('form.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errors.password}
                  placeholder={t('form.enterPassword')}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <Button
                type="submit"
                className="w-full"
                loading={isLoading}
                disabled={isLoading}
              >
                {t('form.signIn')}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    {t('form.orContinueWith')}
                  </span>
                </div>
              </div>

              <GoogleOAuth 
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                className="w-full"
              />
            </form>
          </Card.Content>
        </Card>

        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('login.noAccount')}{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
            >
              {t('login.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage